/**
 * 动态池子算法
 *
 * 原理:
 *   - 给每注打分(0-100)
 *   - 信心高(分数集中):池子缩到 250
 *   - 信心中:池子 500
 *   - 信心低(分数分散):池子扩到 800
 *
 * 信心指数 = (top10 平均分 - 后 100 平均分) / 总分差
 */

import { predictAll, topN } from './mlPredict.js';
import * as formulas from './formulas.js';

// 给单个三码打分(0-100)
function scoreTriple(triple, formulaScores, mlProbs) {
  // 1. 公式投票
  let formulaSum = 0;
  let formulaCount = 0;
  for (const fs of formulaScores) {
    if (fs && fs.scores && fs.scores[triple] != null) {
      formulaSum += fs.scores[triple];
      formulaCount++;
    }
  }
  const formulaAvg = formulaCount ? formulaSum / formulaCount : 0.5;

  // 2. ML 概率
  const mlProb = (mlProbs && mlProbs[triple]) || 0.5;

  // 3. 综合分
  return (formulaAvg * 0.4 + mlProb * 0.6) * 100;
}

// 计算信心指数(0-1)
function calcConfidence(scored) {
  if (scored.length < 100) return 0.5;
  const top10Avg = scored.slice(0, 10).reduce((s, x) => s + x.score, 0) / 10;
  const last100Avg = scored.slice(-100).reduce((s, x) => s + x.score, 0) / 100;
  const overall = scored.reduce((s, x) => s + x.score, 0) / scored.length;
  // 头部突出度
  const conf = (top10Avg - last100Avg) / (overall + 1);
  return Math.max(0, Math.min(1, conf * 2));
}

/**
 * 生成动态池子
 */
export function generateDynamicPool({ recent, userKills = new Set(), userKillPairs = new Set() } = {}) {
  if (!recent || recent.length < 30) {
    return { error: '历史数据不足(需要 30 期+)' };
  }

  // 1. 跑公式,收集每个号的分数
  const formulaResults = [];
  const formulaNames = [
    'mod10', 'positionKill', 'killSumSpan', 'killXingtai', 'killAdjacent',
    'route012', 'heatRatio', 'killAdjSpan', 'zuXuanRef', 'shiWeiZhouSha'
  ];
  for (const fn of formulaNames) {
    try {
      const r = formulas.run(fn, recent, { topK: 100 });
      if (r && r.scores) formulaResults.push(r);
    } catch (e) {}
  }

  // 2. ML 预测
  let mlProbs = {};
  try {
    const mlRes = predictAll(recent);
    if (Array.isArray(mlRes)) {
      mlRes.forEach((p, i) => {
        const code = String(i).padStart(3, '0');
        mlProbs[code] = p;
      });
    }
  } catch (e) {}

  // 3. 全量池子(1000 注)
  const allTriples = [];
  for (let i = 0; i < 1000; i++) {
    allTriples.push(String(i).padStart(3, '0'));
  }

  // 4. 评分
  const scored = allTriples.map(triple => {
    // 杀号过滤:用户杀的单码 / 两码,直接 0 分
    const [a, b, c] = triple.split('').map(Number);
    let userKillPenalty = 0;
    if (userKills.has(a) || userKills.has(b) || userKills.has(c)) userKillPenalty = 100;
    if (userKillPairs.has(`${a}${b}`) || userKillPairs.has(`${a}${c}`) || userKillPairs.has(`${b}${c}`)) userKillPenalty = 100;
    return {
      triple,
      score: Math.max(0, scoreTriple(triple, formulaResults, mlProbs) - userKillPenalty),
    };
  }).sort((a, b) => b.score - a.score);

  // 5. 信心
  const confidence = calcConfidence(scored);

  // 6. 选池子
  let size, tier, label, color;
  if (confidence >= 0.7) {
    size = 250;
    tier = 'D1';
    label = '高信心 · 250 注';
    color = 'text-brand-gold';
  } else if (confidence >= 0.4) {
    size = 500;
    tier = 'D2';
    label = '中信心 · 500 注';
    color = 'text-brand-cyan';
  } else {
    size = 800;
    tier = 'D3';
    label = '低信心 · 800 注(观望)';
    color = 'text-ink-2';
  }

  const pool = scored.slice(0, size).map(x => x.triple);
  const topScore = scored[0]?.score || 0;
  const bottomScore = scored[size - 1]?.score || 0;

  return {
    pool,
    scored: scored.slice(0, size),
    confidence,
    size,
    tier,
    label,
    color,
    top_score: topScore,
    bottom_score: bottomScore,
    score_gap: topScore - bottomScore,
    explanation: {
      confidence_level: confidence >= 0.7 ? 'high' : confidence >= 0.4 ? 'medium' : 'low',
      formula_count: formulaResults.length,
      ml_used: Object.keys(mlProbs).length > 0,
      user_kills: userKills.size,
      user_kill_pairs: userKillPairs.size,
    },
  };
}

/**
 * 信心等级 + 建议
 */
export function getConfidenceAdvice(confidence) {
  if (confidence >= 0.7) {
    return {
      level: 'high',
      emoji: '🔥',
      title: '高信心',
      advice: '建议出击:池子缩到 250,期望命中率 75-80%',
      action: 'buy',
      color: 'text-brand-gold',
    };
  }
  if (confidence >= 0.4) {
    return {
      level: 'medium',
      emoji: '⚖️',
      title: '中信心',
      advice: '标准打法:池子 500,期望命中率 63-67%',
      action: 'buy',
      color: 'text-brand-cyan',
    };
  }
  return {
    level: 'low',
    emoji: '⏸️',
    title: '低信心',
    advice: '建议观望:池子 800 命中率 55%,跳过本期更划算',
    action: 'skip',
    color: 'text-ink-2',
  };
}
